import { useParams, Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allManga, categoryNameMap } from '@/mocks/manga';
import { categories } from '@/mocks/homepage';
import SEOHead from '@/components/base/SEOHead';
import { useState, useEffect } from 'react';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryName = categoryNameMap[slug] || '全部漫画';
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'latest');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  useEffect(() => {
    const newSort = searchParams.get('sort') || 'latest';
    const newStatus = searchParams.get('status') || 'all';
    setSortBy(newSort);
    setStatusFilter(newStatus);
  }, [searchParams]);

  const updateSort = (newSort: string) => {
    setSortBy(newSort);
    const params = new URLSearchParams(searchParams);
    if (newSort === 'latest') {
      params.delete('sort');
    } else {
      params.set('sort', newSort);
    }
    setSearchParams(params, { replace: true });
  };

  const updateStatus = (newStatus: string) => {
    setStatusFilter(newStatus);
    const params = new URLSearchParams(searchParams);
    if (newStatus === 'all') {
      params.delete('status');
    } else {
      params.set('status', newStatus);
    }
    setSearchParams(params, { replace: true });
  };

  let filteredManga = slug === 'all'
    ? allManga
    : allManga.filter((m) => m.categorySlug === slug);

  if (statusFilter === 'ongoing') {
    filteredManga = filteredManga.filter((m) => m.status === '连载中');
  } else if (statusFilter === 'completed') {
    filteredManga = filteredManga.filter((m) => m.status === '已完结');
  }

  if (sortBy === 'rating') {
    filteredManga = [...filteredManga].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'popular') {
    filteredManga = [...filteredManga].sort((a, b) => {
      const aViews = parseInt(a.views.replace(/[万+]/g, ''), 10);
      const bViews = parseInt(b.views.replace(/[万+]/g, ''), 10);
      return bViews - aViews;
    });
  }

  const canonicalUrl = `http://zaixinjiaju.com/category/${slug}`;
  const seoData = {
    title: `${categoryName}_韩漫在线 - 韩国漫画免费阅读平台`,
    description: `韩漫在线${categoryName}分类，收录${filteredManga.length}部韩国漫画，免费在线观看，高清画质，每日更新。`,
    keywords: `韩漫在线,${categoryName},韩国漫画,免费漫画,在线漫画`,
    canonicalUrl,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://zaixinjiaju.com/' },
          { '@type': 'ListItem', 'position': 2, 'name': categoryName, 'item': canonicalUrl },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${canonicalUrl}#webpage`,
        'url': canonicalUrl,
        'name': `${categoryName} - 韩漫在线`,
        'description': `韩漫在线${categoryName}分类，收录${filteredManga.length}部韩国漫画，免费在线观看，高清画质，每日更新。`,
        'isPartOf': { '@id': 'http://zaixinjiaju.com/#website' },
        'about': { '@id': 'http://zaixinjiaju.com/#organization' },
      },
    ],
  };

  const breadcrumbs = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${slug}` },
  ];

  return (
    <>
      <SEOHead {...seoData} />
      <Navbar />
      {/* ... rest of component ... */}
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-3 flex-wrap" aria-label="面包屑导航">
              {breadcrumbs.map((crumb, idx) => (
                <span key={crumb.href} className="flex items-center gap-2">
                  {idx > 0 && <span className="text-foreground-300">/</span>}
                  {idx < breadcrumbs.length - 1 ? (
                    <Link to={crumb.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-foreground-950 font-medium whitespace-nowrap">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {filteredManga.length} 部作品
            </p>
          </div>
        </div>

        <div className="w-full bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 flex-wrap">
                <Link
                  to="/category/all"
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    slug === 'all' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  全部
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      slug === cat.slug ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <div className="flex items-center gap-3 ml-auto flex-shrink-0">
                <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                  <button
                    onClick={() => updateStatus('all')}
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      statusFilter === 'all' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    全部
                  </button>
                  <button
                    onClick={() => updateStatus('ongoing')}
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      statusFilter === 'ongoing' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    连载中
                  </button>
                  <button
                    onClick={() => updateStatus('completed')}
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      statusFilter === 'completed' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    已完结
                  </button>
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => updateSort(e.target.value)}
                  className="text-xs bg-background-100 border border-background-200 rounded-full px-3 py-1.5 text-foreground-600 outline-none cursor-pointer focus:border-primary-400 transition-colors"
                >
                  <option value="latest">最新</option>
                  <option value="rating">评分最高</option>
                  <option value="popular">最受欢迎</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          {filteredManga.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-file-search-line text-6xl text-foreground-300 mb-4" />
              <p className="text-foreground-500 text-sm">暂无相关内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {filteredManga.map((manga) => (
                <Link
                  key={manga.id}
                  to="/category/all"
                  className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="p-3">
                    <div className="w-full aspect-[3/4] overflow-hidden rounded-lg mb-3">
                      <img
                        src={manga.cover}
                        alt={manga.title}
                        title={`${manga.title} - ${manga.category} - 韩漫在线`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 truncate whitespace-nowrap">
                      {manga.title}
                    </h3>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{manga.category}</span>
                      <span className="flex items-center gap-0.5 text-xs text-primary-600 whitespace-nowrap">
                        <i className="ri-star-fill text-xs" />
                        {manga.rating}
                      </span>
                    </div>
                    <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                      manga.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {manga.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}